import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  Pencil,
  Receipt,
  Trash2,
  X,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Image,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useConfirm } from '@/components/ConfirmDialog';
import { DatePickerSheet } from '@/components/DatePickerSheet';
import { DocumentPreview } from '@/components/DocumentPreview';
import { PdfThumb } from '@/components/PdfThumb';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { DocumentRow, ExpenseRecurring, ExpenseRow } from '@/db/types';
import { parseLocalDate, toLocalYMD } from '@/lib/dates';
import { getDocumentById } from '@/lib/documents';
import {
  allExpenseCategories,
  RECURRING_OPTIONS,
  deleteExpense,
  getExpenseById,
  updateExpense,
} from '@/lib/expenses';
import { isImageMime } from '@/lib/files';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

/**
 * Expense detail with inline edit. The amount and date are usually the
 * fields people fix; everything else is rare. Edit toggle keeps the view
 * mode visually quiet so people aren't scared by an always-editable form.
 */
export default function ExpenseDetail() {
  const router = useRouter();
  const db = useSQLiteContext();
  // Wave 61: user-defined categories join the picker. Loaded once from
  // the active property row.
  const [customCatsJson, setCustomCatsJson] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      const p = await getActiveProperty(db);
      setCustomCatsJson(p?.custom_expense_categories_json ?? null);
    })();
  }, [db]);
  const confirm = useConfirm();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [row, setRow] = useState<ExpenseRow | null>(null);
  const [receipt, setReceipt] = useState<DocumentRow | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [editing, setEditing] = useState(false);

  // Editable buffers (only used when editing=true).
  const [amount, setAmount] = useState('');
  const [vendor, setVendor] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<string>('Utilities');
  const [paidOn, setPaidOn] = useState('');
  const [recurring, setRecurring] = useState<ExpenseRecurring | null>(null);
  const [taxDeductible, setTaxDeductible] = useState(false);
  const [notes, setNotes] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [recOpen, setRecOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const r = await getExpenseById(db, id);
      setRow(r);
      if (r) hydrateBuffers(r);
      // Hydrate the receipt document if this expense has one attached
      // (set when the user imported via Scan receipts, or attached
      // manually). Surfaces as a tappable thumbnail in view mode.
      if (r?.receipt_doc_id) {
        const doc = await getDocumentById(db, r.receipt_doc_id);
        setReceipt(doc);
      } else {
        setReceipt(null);
      }
    })();
  }, [db, id]);

  const hydrateBuffers = (r: ExpenseRow) => {
    setAmount((r.amount_cents / 100).toFixed(2));
    setVendor(r.vendor ?? '');
    setTitle(r.description ?? '');
    setCategory(r.category);
    setPaidOn(toLocalYMD(r.paid_on));
    setRecurring(r.recurring);
    setTaxDeductible(r.tax_deductible === 1);
    setNotes(r.notes ?? '');
  };

  if (!row) return <View style={styles.container} />;

  const confirmDelete = async () => {
    const ok = await confirm({
      title: 'Delete this expense?',
      message: 'It will disappear from totals and the timeline.',
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (ok) await doDelete();
  };

  const doDelete = async () => {
    setWorking(true);
    try {
      await deleteExpense(db, row.id);
      goBackOrTo(router, '/expenses');
    } finally {
      setWorking(false);
    }
  };

  const save = async () => {
    const cents = Math.round(parseFloat(amount.replace(/[^0-9.]/g, '')) * 100);
    if (isNaN(cents) || cents <= 0) return;
    const paid = parseLocalDate(paidOn);
    if (isNaN(paid)) return;
    setWorking(true);
    try {
      await updateExpense(db, row.id, {
        amount_cents: cents,
        vendor: vendor.trim() || null,
        description: title.trim() || null,
        category,
        paid_on: paid,
        recurring,
        tax_deductible: taxDeductible ? 1 : 0,
        notes: notes.trim() || null,
      });
      const fresh = await getExpenseById(db, row.id);
      if (fresh) {
        setRow(fresh);
        hydrateBuffers(fresh);
      }
      setEditing(false);
    } finally {
      setWorking(false);
    }
  };

  const cancel = () => {
    hydrateBuffers(row);
    setEditing(false);
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/expenses')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Expense</Text>
        {editing ? (
          <Pressable
            onPress={cancel}
            style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <X size={20} color={Palette.ink} strokeWidth={2} />
          </Pressable>
        ) : (
          <Pressable
            onPress={() => setEditing(true)}
            style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
            hitSlop={12}>
            <Pencil size={18} color={Palette.ink} strokeWidth={1.8} />
          </Pressable>
        )}
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 60 }}>
        <View style={styles.hero}>
          {editing ? (
            <TextInput
              value={amount}
              onChangeText={setAmount}
              placeholder="0.00"
              placeholderTextColor="rgba(251,246,234,0.5)"
              inputMode="decimal"
              style={[styles.amountInput, webNoOutline]}
            />
          ) : (
            <Text style={styles.amount}>
              ${(row.amount_cents / 100).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Text>
          )}
          <Text style={styles.heroMeta}>
            {row.vendor ?? row.category}
            {' · '}
            {new Date(row.paid_on).toLocaleDateString(undefined, {
              weekday: 'short',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </Text>
        </View>

        <View style={styles.card}>
          {editing ? (
            <>
              <FieldEdit label="Title" value={title} onChangeText={setTitle} placeholder="e.g. May electric bill" />
              <FieldEdit label="Vendor" value={vendor} onChangeText={setVendor} placeholder="DTE Energy" />
              <PickEdit label="Category" value={category} onPress={() => setCatOpen(true)} />
              {/* Calendar picker (Wave 48) — parity with the create
                  form. The old free-text YYYY-MM-DD field silently
                  aborted the save on a typo. */}
              <PickEdit
                label="Paid on"
                value={(() => {
                  const t = parseLocalDate(paidOn);
                  return isNaN(t)
                    ? 'Pick a date'
                    : new Date(t).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric', year: 'numeric',
                      });
                })()}
                onPress={() => setDateOpen(true)}
              />
              <PickEdit
                label="Recurring"
                value={recurring ? RECURRING_OPTIONS.find((o) => o.v === recurring)?.label ?? null : 'One-time'}
                onPress={() => setRecOpen(true)}
              />
              <Pressable onPress={() => setTaxDeductible(!taxDeductible)} style={styles.toggleRow}>
                <Text style={styles.rowLabelEdit}>Tax-deductible</Text>
                <View style={[styles.toggle, taxDeductible && styles.toggleOn]}>
                  <View style={[styles.toggleThumb, taxDeductible && styles.toggleThumbOn]} />
                </View>
              </Pressable>
              <View style={{ marginTop: Spacing.three }}>
                <Text style={styles.fieldLabelEdit}>NOTES</Text>
                <TextInput
                  value={notes}
                  onChangeText={setNotes}
                  placeholder="Optional"
                  placeholderTextColor={Palette.inkFaint}
                  multiline
                  style={[styles.fieldInputEdit, webNoOutline, { height: 80, textAlignVertical: 'top' }]}
                />
              </View>
            </>
          ) : (
            <>
              <Row label="Category" value={row.category} />
              {row.vendor ? <Row label="Vendor" value={row.vendor} /> : null}
              {row.description ? <Row label="Title" value={row.description} /> : null}
              {row.recurring ? <Row label="Recurring" value={row.recurring} /> : null}
              {row.tax_deductible === 1 ? <Row label="Tax-deductible" value="Yes" /> : null}
              {row.notes ? <Row label="Notes" value={row.notes} last={!receipt} /> : null}
            </>
          )}
        </View>

        {/* Receipt thumbnail — tap to open the full-size viewer with
            download / share. Same DocumentPreview the /media page uses,
            so behavior is consistent across the app. Only renders in
            view mode; editing the linked doc is done through /media. */}
        {!editing && receipt ? (
          <Pressable
            onPress={() => setPreviewOpen(true)}
            style={({ pressed }) => [
              styles.receiptCard,
              pressed && { transform: [{ scale: 0.99 }], opacity: 0.95 },
            ]}
            accessibilityLabel="View receipt">
            {/* Wave 53: full-width document preview — a postage-stamp
                thumb made users squint; the attached paper IS the
                content here, so show it big. Tap still opens the
                full-size viewer. */}
            {isImageMime(receipt.mime_type) ? (
              <Image source={{ uri: receipt.file_uri }} style={styles.receiptHero} resizeMode="cover" />
            ) : receipt.mime_type?.includes('pdf') ? (
              <PdfThumb
                uri={receipt.file_uri}
                style={styles.receiptHero}
                fallback={
                  <View style={[styles.receiptHero, styles.receiptThumbFallback]}>
                    <Receipt size={40} color={Palette.forest} strokeWidth={1.4} />
                  </View>
                }
              />
            ) : (
              <View style={[styles.receiptHero, styles.receiptThumbFallback]}>
                <Receipt size={40} color={Palette.forest} strokeWidth={1.4} />
              </View>
            )}
            <View style={styles.receiptMetaRow}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.receiptLabel}>RECEIPT</Text>
                <Text style={styles.receiptTitle} numberOfLines={2}>{receipt.title}</Text>
                <Text style={styles.receiptHint}>Tap to view full size · download</Text>
              </View>
            </View>
          </Pressable>
        ) : null}

        {editing ? (
          <Pressable
            onPress={save}
            disabled={working}
            style={({ pressed }) => [
              styles.saveBtn,
              working && { opacity: 0.5 },
              pressed && { transform: [{ scale: 0.98 }] },
            ]}>
            <Check size={18} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.saveText}>{working ? 'Saving…' : 'Save changes'}</Text>
          </Pressable>
        ) : (
          <Pressable
            onPress={confirmDelete}
            disabled={working}
            style={({ pressed }) => [styles.removeBtn, pressed && { opacity: 0.85 }]}>
            <Trash2 size={16} color={Palette.clay} strokeWidth={1.8} />
            <Text style={styles.removeText}>{working ? 'Removing…' : 'Delete expense'}</Text>
          </Pressable>
        )}
      </ScrollView>

      <Modal animationType="fade" transparent visible={catOpen} onRequestClose={() => setCatOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setCatOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Category</Text>
            <ScrollView style={{ maxHeight: 380 }}>
              {allExpenseCategories(customCatsJson).map((c) => (
                <Pressable
                  key={c}
                  onPress={() => { setCategory(c); setCatOpen(false); }}
                  style={({ pressed }) => [
                    styles.modalRow,
                    (pressed || category === c) && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <Text style={styles.modalRowText}>{c}</Text>
                </Pressable>
              ))}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      <DocumentPreview
        doc={previewOpen ? receipt : null}
        onClose={() => setPreviewOpen(false)}
      />

      <DatePickerSheet
        open={dateOpen}
        value={(() => { const t = parseLocalDate(paidOn); return isNaN(t) ? null : t; })()}
        onPick={(ms) => {
          setPaidOn(toLocalYMD(ms));
          setDateOpen(false);
        }}
        onClose={() => setDateOpen(false)}
        title="Paid on"
      />

      <Modal animationType="fade" transparent visible={recOpen} onRequestClose={() => setRecOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setRecOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Recurring?</Text>
            <Pressable
              onPress={() => { setRecurring(null); setRecOpen(false); }}
              style={({ pressed }) => [
                styles.modalRow,
                (pressed || recurring === null) && { backgroundColor: '#EEF3EE' },
              ]}>
              <Text style={styles.modalRowText}>One-time</Text>
            </Pressable>
            {RECURRING_OPTIONS.map((o) => (
              <Pressable
                key={o.v}
                onPress={() => { setRecurring(o.v); setRecOpen(false); }}
                style={({ pressed }) => [
                  styles.modalRow,
                  (pressed || recurring === o.v) && { backgroundColor: '#EEF3EE' },
                ]}>
                <Text style={styles.modalRowText}>{o.label}</Text>
              </Pressable>
            ))}
          </Pressable>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}

function Row({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <View style={[styles.rowItem, last && { borderBottomWidth: 0 }]}>
      <Text style={styles.rowLabel}>{label.toUpperCase()}</Text>
      <Text style={styles.rowValue}>{value}</Text>
    </View>
  );
}

function FieldEdit({
  label, value, onChangeText, placeholder,
}: { label: string; value: string; onChangeText: (v: string) => void; placeholder?: string }) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabelEdit}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        style={[styles.fieldInputEdit, webNoOutline]}
      />
    </View>
  );
}

function PickEdit({ label, value, onPress }: { label: string; value: string | null; onPress: () => void }) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabelEdit}>{label.toUpperCase()}</Text>
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
        <Text style={styles.pickText}>{value ?? 'Pick…'}</Text>
        <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  hero: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    backgroundColor: Palette.forest,
    borderRadius: Radius.xl,
    padding: 24,
    alignItems: 'center',
  },
  amount: {
    fontFamily: Fonts.serifBold,
    fontSize: 44,
    color: Palette.cream,
    letterSpacing: -1.2,
  },
  amountInput: {
    fontFamily: Fonts.serifBold,
    fontSize: 44,
    color: Palette.cream,
    letterSpacing: -1.2,
    textAlign: 'center',
    paddingVertical: 4,
    minWidth: 160,
  },
  heroMeta: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: 'rgba(251,246,234,0.8)',
    marginTop: 4,
    textAlign: 'center',
  },
  card: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, paddingHorizontal: 16, paddingBottom: 4,
  },
  rowItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
    gap: 12,
  },
  rowLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.inkFaint,
    letterSpacing: 0.5,
  },
  rowValue: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13.5,
    color: Palette.ink,
    flex: 1,
    textAlign: 'right',
  },
  fieldLabelEdit: {
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  fieldInputEdit: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  pickBox: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 12,
  },
  pickText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  rowLabelEdit: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink, flex: 1 },
  toggleRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    marginTop: Spacing.three,
    padding: 12,
    backgroundColor: Palette.sand,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  toggle: {
    width: 40, height: 24, borderRadius: 12,
    backgroundColor: Palette.sandDeep, padding: 3, justifyContent: 'center',
  },
  toggleOn: { backgroundColor: Palette.forest },
  toggleThumb: { width: 18, height: 18, borderRadius: 9, backgroundColor: Palette.cream },
  toggleThumbOn: { transform: [{ translateX: 16 }] },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
  removeBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, paddingVertical: 14,
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
  },
  removeText: { color: Palette.clay, fontFamily: Fonts.sansBold, fontSize: 13.5 },
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 18, width: '100%', maxWidth: 380,
  },
  modalTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink, marginBottom: 10 },
  modalRow: { paddingVertical: 11, paddingHorizontal: 12, borderRadius: 10 },
  modalRowText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },

  // Receipt thumbnail card — sits below the expense detail card in view
  // mode when the expense has a linked receipt document. Tap opens the
  // shared DocumentPreview viewer (download / share is built-in there).
  receiptCard: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    overflow: 'hidden',
  },
  receiptHero: {
    width: '100%',
    height: 300,
    backgroundColor: '#fff',
  },
  receiptMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 14,
    borderTopWidth: 1,
    borderTopColor: Palette.line,
  },
  receiptThumbFallback: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EAF0EA',
  },
  receiptLabel: {
    fontFamily: Fonts.sansBold, fontSize: 11, color: Palette.honeyDeep,
    letterSpacing: 0.7,
  },
  receiptTitle: {
    marginTop: 4, fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink,
    letterSpacing: -0.1,
  },
  receiptHint: {
    marginTop: 4, fontFamily: Fonts.sansMedium, fontSize: 12.5,
    color: Palette.inkSoft, letterSpacing: 0.2,
  },
});
